/**
 * 合集 action 编排：运营配 repeat_count、rest_seconds；
 * 落库字段仅 5 个：resource_id / repeat_count / rest_seconds / video_duration_seconds / duration_seconds。
 * <p>
 * duration_seconds 派生（保存时计算，不再另存冗余副本）：
 * <ul>
 *   <li>单视角：repeat_count × 主视角时长</li>
 *   <li>双视角：repeat_count ×（主视角时长 + 侧视角时长）</li>
 * </ul>
 * video_duration_seconds 仍只存主视角快照；侧视角时长来自 ref_meta，不单独落库。
 */

import { createBindingRowUid } from './workoutBindingRowUid'

/** 单条动作允许的最大循环次数（video_loop=true 时） */
export const MAX_ACTION_REPEAT_COUNT = 999

/** video_loop=false 时 repeat_count 上限 */
export const MAX_ACTION_REPEAT_COUNT_NO_LOOP = 1

/** 单条休息秒数上限（业务护栏） */
export const MAX_REST_SECONDS = 600

/** 新选/复制动作的默认休息秒（末条仍由 normalize 清零；现网非 0 休息约 94% 为 10） */
export const DEFAULT_ACTION_REST_SECONDS = 10

/**
 * 落库时随动作行写入的派生字段（不含运营手填的 resource_id / repeat / rest / duration）。
 * sanitize / preservePersisted 共用，避免 key 列表漂移。
 */
export const ACTION_ADAPTATION_FIELD_KEYS = ['video_duration_seconds']

export function pickActionAdaptationFields(row) {
  const picked = {}
  ACTION_ADAPTATION_FIELD_KEYS.forEach((key) => {
    picked[key] = row[key]
  })
  return picked
}

export function round1(value) {
  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed)) {
    return 0
  }
  return Math.round(parsed * 10) / 10
}

export function parseRepeatCount(value, fallback = 0) {
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback
  }
  return Math.min(parsed, MAX_ACTION_REPEAT_COUNT)
}

export function parseRestSeconds(value, fallback = 0) {
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback
  }
  return Math.min(parsed, MAX_REST_SECONDS)
}

/** 从 binding ref_meta 读取动作资源 video_loop；null 表示未知，不收紧上限。 */
export function readVideoLoopFromBindingRow(row) {
  const meta = row && row.ref_meta
  if (!meta || typeof meta !== 'object') {
    return null
  }
  if (typeof meta.videoLoop === 'boolean') {
    return meta.videoLoop
  }
  if (typeof meta.video_loop === 'boolean') {
    return meta.video_loop
  }
  return null
}

export function maxRepeatCountForRow(row) {
  if (readVideoLoopFromBindingRow(row) === false) {
    return MAX_ACTION_REPEAT_COUNT_NO_LOOP
  }
  return MAX_ACTION_REPEAT_COUNT
}

export function parseRepeatCountForRow(row, value, fallback = 0) {
  const parsed = parseRepeatCount(value, fallback)
  return Math.min(parsed, maxRepeatCountForRow(row))
}

export function isRepeatCountMissing(row) {
  if (!row) {
    return true
  }
  return row.repeat_count == null || row.repeat_count === ''
}

export function readVideoDurationSeconds(refMeta) {
  if (!refMeta || typeof refMeta !== 'object') {
    return null
  }
  const raw =
    refMeta.videoDurationSeconds != null
      ? refMeta.videoDurationSeconds
      : refMeta.video_duration_seconds
  const parsed = Number.parseFloat(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  return round1(parsed)
}

/** 侧视角时长；无侧视角或无效时返回 null。 */
export function readVideoSideDurationSeconds(refMeta) {
  if (!refMeta || typeof refMeta !== 'object') {
    return null
  }
  const raw =
    refMeta.videoSideDurationSeconds != null
      ? refMeta.videoSideDurationSeconds
      : refMeta.video_side_duration_seconds
  const parsed = Number.parseFloat(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  return round1(parsed)
}

/**
 * 是否双视角：优先看 ref_meta.hasSideView；旧快照缺字段时不因侧时长单独判定为双视角。
 */
export function hasSideViewFromRefMeta(refMeta) {
  if (!refMeta || typeof refMeta !== 'object') {
    return false
  }
  if (typeof refMeta.hasSideView === 'boolean') {
    return refMeta.hasSideView
  }
  return false
}

/**
 * 解析动作视频时长 V：优先 ref_meta（实时），其次 binding 行已落库的 video_duration_seconds。
 */
export function readVideoDurationFromBindingRow(row) {
  if (!row) {
    return null
  }
  const fromRefMeta = readVideoDurationSeconds(row.ref_meta)
  if (fromRefMeta != null) {
    return fromRefMeta
  }
  const parsed = Number.parseFloat(row.video_duration_seconds)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }
  return round1(parsed)
}

export function readVideoSideDurationFromBindingRow(row) {
  if (!row) {
    return null
  }
  return readVideoSideDurationSeconds(row.ref_meta)
}

export function hasSideViewFromBindingRow(row) {
  return hasSideViewFromRefMeta(row && row.ref_meta)
}

/** 主≠侧时提示运营：总时长仍按（主+侧）×次数，两边时长不一致仅作告知。 */
export function shouldWarnMainSideDurationMismatch(row) {
  if (!hasSideViewFromBindingRow(row)) {
    return false
  }
  const main = readVideoDurationFromBindingRow(row)
  const side = readVideoSideDurationFromBindingRow(row)
  if (main == null || side == null) {
    return false
  }
  return main !== side
}

/**
 * 单次播放单元时长：单视角=主；双视角=主+侧。
 * 双视角缺侧时长时返回 null（不可编排）。
 */
export function resolveActionPlayUnitSeconds(row) {
  const main = readVideoDurationFromBindingRow(row)
  if (main == null) {
    return null
  }
  if (!hasSideViewFromBindingRow(row)) {
    return main
  }
  const side = readVideoSideDurationFromBindingRow(row)
  if (side == null) {
    return null
  }
  return round1(main + side)
}

export function computeConfiguredPlaySeconds(repeatCount, videoSeconds) {
  const video = Number.parseFloat(videoSeconds)
  const repeat = Number.parseInt(repeatCount, 10)
  if (!Number.isFinite(video) || video <= 0 || !Number.isFinite(repeat) || repeat < 0) {
    return 0
  }
  return round1(repeat * video)
}

/**
 * duration_seconds 由 repeat × 单次单元时长派生。
 * @param {number} repeatCount
 * @param {number} videoSeconds 主视角；或已算好的单元时长（兼容旧调用）
 * @param {number|null} [sideSeconds] 侧视角；传入且有效时按双视角 (主+侧)×次数
 */
export function deriveDurationSecondsFromRepeat(repeatCount, videoSeconds, sideSeconds = null) {
  const main = Number.parseFloat(videoSeconds)
  if (!Number.isFinite(main) || main <= 0) {
    return 0
  }
  const side = Number.parseFloat(sideSeconds)
  const unit =
    Number.isFinite(side) && side > 0 ? round1(main + side) : main
  return computeConfiguredPlaySeconds(repeatCount, unit)
}

/**
 * 从 binding 行解析并回写 video_duration_seconds（主视角单次时长快照，供落库与展示）。
 */
export function buildVideoAdaptationFields(row) {
  const videoSeconds = readVideoDurationFromBindingRow(row)
  return {
    video_duration_seconds: videoSeconds,
  }
}

/**
 * 是否应清零/禁用休息：仅当动作数 ≥ 2 且为最后一条时（单条动作仍可配置休息，便于后续追加动作）。
 */
export function shouldClearRestForAction(index, totalRows) {
  return totalRows > 1 && index === totalRows - 1
}

/**
 * 单一重算入口：改 repeat / rest / ref_meta 补全 / sanitize 均走此函数。
 * @param {object} row binding 行
 * @param {{ clearRest?: boolean, isLast?: boolean, index?: number, totalRows?: number }} [options]
 *   clearRest / isLast=true 时强制 rest_seconds=0（isLast 兼容旧调用，优先用 index+totalRows）
 */
export function recomputeActionBindingRow(row, options = {}) {
  const playUnitSeconds = resolveActionPlayUnitSeconds(row)
  const repeat = playUnitSeconds ? parseRepeatCountForRow(row, row.repeat_count, 0) : 0
  const durationSeconds = playUnitSeconds
    ? computeConfiguredPlaySeconds(repeat, playUnitSeconds)
    : 0
  const clearRest =
    options.clearRest ??
    (options.index != null && options.totalRows != null
      ? shouldClearRestForAction(options.index, options.totalRows)
      : Boolean(options.isLast))
  const restSeconds = clearRest ? 0 : parseRestSeconds(row.rest_seconds, 0)

  const merged = {
    ...row,
    duration_seconds: durationSeconds,
    repeat_count: repeat,
    rest_seconds: restSeconds,
  }
  return {
    ...merged,
    ...buildVideoAdaptationFields(merged),
  }
}

/** CMS 编辑态是否已挂上子资源说明书（与列表 :key 的 uid 无关）。 */
export function hasBindingRefMeta(row) {
  return Boolean(row && row.ref_meta && typeof row.ref_meta === 'object')
}

/** ref_meta 序列化签名：用于 refresh 前后比较是否需写回 UI。 */
export function bindingRefMetaSignature(row) {
  return JSON.stringify((row && row.ref_meta) || null)
}

/**
 * 无说明书时重算只会按主视角；若行上已有落库 duration，先保留，等 refresh 补齐后再重算。
 * 加载（normalizeActionRow）与列表归一化（normalizeActionBindingsList / 拖拽）共用，避免拖一下把双视角压矮。
 */
export function preservePersistedDurationIfNoRefMeta(sourceRow, recomputed) {
  if (hasBindingRefMeta(sourceRow)) {
    return recomputed
  }
  const persisted = Number(sourceRow && sourceRow.duration_seconds)
  if (Number.isFinite(persisted) && persisted > 0) {
    return { ...recomputed, duration_seconds: round1(persisted) }
  }
  return recomputed
}

/** 列表级归一化：多条动作时最后一条休息清零，并统一重算。 */
export function normalizeActionBindingsList(actions) {
  if (!Array.isArray(actions) || !actions.length) {
    return []
  }
  return actions.map((row, index) => {
    const recomputed = recomputeActionBindingRow(row, { index, totalRows: actions.length })
    return preservePersistedDurationIfNoRefMeta(row, recomputed)
  })
}

/**
 * 动作区展示用总时长：说明书齐了按实时重算；否则信任已落库 duration（双视角加载瞬间）。
 */
export function resolveActionZoneTotalSeconds(actions) {
  const rows = Array.isArray(actions) ? actions : []
  if (rows.length && rows.every((row) => hasBindingRefMeta(row))) {
    return computeTotalDurationSeconds(rows)
  }
  return sumActionDurationSeconds(rows)
}

/**
 * 对「已算好 duration_seconds」的动作行求和：Σ运动 + Σ中间休息（不含最后一条休息）。
 * 不读 ref_meta、不重算——适合 sanitize 后的瘦身行，或加载瞬间尚未挂上说明书的行。
 *
 * @param {Array<object>} actions sanitize 后或其它已带正确 duration_seconds 的行
 * @returns {number}
 */
export function sumActionDurationSeconds(actions) {
  const rows = Array.isArray(actions) ? actions : []
  let total = 0
  rows.forEach((row, index) => {
    total += Number(row.duration_seconds) || 0
    if (index < rows.length - 1) {
      total += parseRestSeconds(row.rest_seconds, 0)
    }
  })
  return round1(total)
}

/**
 * 课程总时长（编辑态）：normalize 后求和。
 * 无说明书时 normalize 会保留已落库 duration（见 {@link preservePersistedDurationIfNoRefMeta}）；
 * 有说明书时按主/侧视角重算。sanitize 后的瘦身行若只要「信行上 duration」，仍可用 {@link sumActionDurationSeconds}。
 */
export function computeTotalDurationSeconds(actions) {
  return sumActionDurationSeconds(normalizeActionBindingsList(actions))
}

export function formatTotalDurationLabel(seconds) {
  const total = round1(seconds)
  if (total <= 0) {
    return '0s'
  }
  if (total < 60) {
    return `${total}s`
  }
  const mins = Math.floor(total / 60)
  const secs = round1(total - mins * 60)
  if (secs === 0) {
    return `${mins} 分`
  }
  return `${mins} 分 ${secs} 秒`
}

/** 从 binding ref_meta 读取动作单次 calories（zip config 写入 exta）；缺失返回 null。 */
export function readCaloriesFromBindingRow(row) {
  const meta = row && row.ref_meta
  if (!meta || typeof meta !== 'object') {
    return null
  }
  const parsed = Number.parseInt(meta.calories, 10)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null
  }
  return parsed
}

/**
 * 课程总卡路里 = Σ(动作单次 calories × 本合集 repeat_count)。
 * 任一动作缺 calories 则整条汇总为 null（避免误显示 0）。
 */
export function computeTotalCalories(actions) {
  const normalized = normalizeActionBindingsList(actions)
  if (!normalized.length) {
    return null
  }
  let total = 0
  for (const row of normalized) {
    const unit = readCaloriesFromBindingRow(row)
    if (unit == null) {
      return null
    }
    const repeat = parseRepeatCount(row.repeat_count, 0)
    total += unit * repeat
  }
  return total
}

export function formatTotalCaloriesLabel(value) {
  if (value == null || !Number.isFinite(value)) {
    return '—'
  }
  if (value <= 0) {
    return '0 kcal'
  }
  return `${value} kcal`
}

/** 新选动作：repeat 从 0 开始，rest 默认 DEFAULT_ACTION_REST_SECONDS。 */
export function initActionBindingRow(row) {
  return recomputeActionBindingRow({
    ...row,
    repeat_count: 0,
    rest_seconds: DEFAULT_ACTION_REST_SECONDS,
  })
}

/** 运营调整视频循环次数。 */
export function applyRepeatCountChange(row, repeatCount, options = {}) {
  return recomputeActionBindingRow(
    {
      ...row,
      repeat_count: parseRepeatCountForRow(row, repeatCount, 0),
    },
    options
  )
}

/** 运营调整休息秒数（最后一条由 isLast 强制为 0）。 */
export function applyRestSecondsChange(row, restSeconds, options = {}) {
  return recomputeActionBindingRow(
    {
      ...row,
      rest_seconds: parseRestSeconds(restSeconds, 0),
    },
    options
  )
}

/**
 * 复制干净绑定行：同一 resource_id，编排参数重置为 repeat=0 / rest=默认；uid 必须新建以免拖拽 key 冲突。
 */
export function duplicateActionBindingRow(row) {
  return initActionBindingRow({
    uid: createBindingRowUid(),
    resource_id: row.resource_id,
    resource_name: row.resource_name,
    resource_db_id: row.resource_db_id ?? null,
    ref_meta: row.ref_meta ? { ...row.ref_meta } : null,
  })
}

export function actionBindingRowsChanged(before, after) {
  if (!Array.isArray(before) || !Array.isArray(after) || before.length !== after.length) {
    return true
  }
  return before.some((row, index) => {
    const next = after[index]
    // refresh 仅补说明书（时长数字不变）也必须写回
    if (bindingRefMetaSignature(row) !== bindingRefMetaSignature(next)) {
      return true
    }
    return round1(row.duration_seconds) !== round1(next.duration_seconds)
  })
}

/** 非动作区：ref_meta 或 music.loop 变化即视为需更新 UI。 */
export function bindingRowsChanged(zoneId, before, after) {
  if (zoneId === 'actions') {
    return actionBindingRowsChanged(before, after)
  }
  if (!Array.isArray(before) || !Array.isArray(after) || before.length !== after.length) {
    return true
  }
  return before.some((row, index) => {
    const next = after[index]
    if (bindingRefMetaSignature(row) !== bindingRefMetaSignature(next)) {
      return true
    }
    return zoneId === 'music' && row.loop !== next.loop
  })
}

/**
 * 绑定行客户端 id：与 {@link ensureBindingRowUid} / 列表 :key 同一字段 `uid`。
 * 曾误用 `_uid`，导致 refresh 结果永远合不进编辑态。
 */
export function readBindingRowUid(row) {
  if (!row || row.uid == null || row.uid === '') {
    return null
  }
  return row.uid
}

/** 按 uid 建索引，供 refresh 合并对齐同一行。 */
export function indexRowsByUid(rows) {
  const byUid = new Map()
  ;(rows || []).forEach((row) => {
    const id = readBindingRowUid(row)
    if (id != null) {
      byUid.set(id, row)
    }
  })
  return byUid
}

/**
 * refresh 返回后合并到「当前最新行」：请求飞行期间用户改过的 repeat/rest/loop 不被旧快照冲掉。
 * @param {string} zoneId
 * @param {Array<object>} snapshotRows 发起请求时的行
 * @param {Array<object>} latestRows 请求返回时的最新行
 * @param {Array<object>} refreshedRows 服务端 enrich 后的行
 */
export function applyBindingRefreshToLatestRows(zoneId, snapshotRows, latestRows, refreshedRows) {
  const refreshedByUid = indexRowsByUid(refreshedRows)
  const snapshotByUid = indexRowsByUid(snapshotRows)

  return (latestRows || []).map((latest) => {
    const id = readBindingRowUid(latest)
    if (id == null) {
      return latest
    }
    const refreshed = refreshedByUid.get(id)
    if (!refreshed) {
      return latest
    }
    const snap = snapshotByUid.get(id)
    if (zoneId === 'actions') {
      const userEdited =
        snap &&
        (Number(snap.repeat_count) !== Number(latest.repeat_count) ||
          Number(snap.rest_seconds) !== Number(latest.rest_seconds))
      if (userEdited) {
        return mergeActionRowWithRefMeta(latest, refreshed.ref_meta)
      }
      return refreshed
    }
    if (zoneId === 'music') {
      const userEdited = snap && snap.loop !== latest.loop
      if (userEdited) {
        return normalizeMusicLoopForRow({ ...latest, ref_meta: refreshed.ref_meta })
      }
      return refreshed
    }
    return { ...latest, ref_meta: refreshed.ref_meta }
  })
}

export function mergeActionRowWithRefMeta(row, refMeta) {
  const merged = { ...row, ref_meta: refMeta }
  const hasVideo = resolveActionPlayUnitSeconds(merged) != null

  if (!hasVideo) {
    return recomputeActionBindingRow(merged)
  }

  const working = isRepeatCountMissing(row) ? { ...merged, repeat_count: 0 } : merged
  return recomputeActionBindingRow(working)
}

/** 编辑页：repeat / rest 步进 + 只读播放总长预览。 */
export function getLiveAdaptationPreview(row) {
  const playUnitSeconds = resolveActionPlayUnitSeconds(row)
  const repeat = parseRepeatCountForRow(row, row.repeat_count, 0)
  const ready = hasBindingRefMeta(row)
  const baseFlags = {
    repeatMax: maxRepeatCountForRow(row),
    videoLoop: readVideoLoopFromBindingRow(row),
    hasSideView: hasSideViewFromBindingRow(row),
    mainSideMismatch: shouldWarnMainSideDurationMismatch(row),
  }

  if (!playUnitSeconds) {
    return {
      configuredPlayLabel: '—',
      hasVideoDuration: false,
      adaptationEditable: false,
      ...baseFlags,
    }
  }

  // 无说明书：展示已落库 duration，禁止改次数/休息（改了会按主视角压矮双视角）
  if (!ready) {
    const persisted = Number(row.duration_seconds)
    return {
      configuredPlayLabel:
        Number.isFinite(persisted) && persisted > 0 ? `${round1(persisted)}s` : '—',
      hasVideoDuration: true,
      adaptationEditable: false,
      ...baseFlags,
    }
  }

  return {
    configuredPlayLabel: formatConfiguredPlayLabel(repeat, playUnitSeconds),
    hasVideoDuration: true,
    adaptationEditable: true,
    ...baseFlags,
  }
}

export function normalizeActionAdaptationFromExta(row) {
  const base = { ...row }
  if (base.rest_seconds != null) {
    base.rest_seconds = parseRestSeconds(base.rest_seconds, 0)
  }
  return base
}

export function formatConfiguredPlayLabel(repeatCount, videoSeconds) {
  const repeat = Number.parseInt(repeatCount, 10)
  if (!Number.isFinite(repeat) || repeat < 0) {
    return '—'
  }
  const video = Number.parseFloat(videoSeconds)
  if (!Number.isFinite(video) || video <= 0) {
    return '—'
  }
  const seconds = computeConfiguredPlaySeconds(repeat, video)
  return `${seconds}s`
}

/** 音乐资源 loopable=false 时合集 loop 必须为 false。 */
export function readMusicLoopableFromBindingRow(row) {
  const meta = row && row.ref_meta
  if (!meta || typeof meta !== 'object') {
    return null
  }
  if (typeof meta.loopable === 'boolean') {
    return meta.loopable
  }
  return null
}

export function normalizeMusicLoopForRow(row) {
  if (readMusicLoopableFromBindingRow(row) === false) {
    return { ...row, loop: false }
  }
  return row
}
